import { zodResolver } from "@hookform/resolvers/zod"
import { isAxiosError } from "axios"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { useValidateMembersCoupon } from "@/queries/hooks/useValidateMembersCoupon"

import { createMemberCouponSchema, MemberCouponFormData } from "../../../schema"

export type ValidatedCoupon = {
  couponCode: string
  couponDesc: string
  customerId: number
}

type UseMemberCouponProps = {
  firstName: string
  lastName: string
  birthDate: string
  onSuccess?: (coupon: ValidatedCoupon) => void
}

export const useMemberCoupon = ({
  firstName,
  lastName,
  birthDate,
  onSuccess,
}: UseMemberCouponProps) => {
  const { t } = useTranslation()
  const {
    handleSubmit,
    control,
    reset,
    setError,
    formState: { errors },
  } = useForm<MemberCouponFormData>({
    resolver: zodResolver(createMemberCouponSchema()),
    defaultValues: {
      couponCode: "",
    },
  })

  const { validateCoupon, isLoading } = useValidateMembersCoupon()

  const onSubmit = (formData: MemberCouponFormData) => {
    if (!firstName?.trim() || !lastName?.trim() || !birthDate?.trim()) {
      setError("couponCode", {
        type: "manual",
        message: t("passHolderDetails.form.errors.personalInformationMissing"),
      })
      return
    }

    validateCoupon(
      {
        firstName,
        lastName,
        birthDate,
        couponCode: formData.couponCode,
      },
      {
        onSuccess: (coupon) => {
          onSuccess?.({
            couponCode: coupon.CouponCode,
            couponDesc: coupon.CouponDesc,
            customerId: coupon.CustomerId,
          })
          reset()
        },
        onError: (error) => {
          const message = isAxiosError(error)
            ? t(
                "passHolderDetails.form.errors.memberCouponValidationNetworkError"
              )
            : error.message

          setError("couponCode", {
            type: "server",
            message,
          })
        },
      }
    )
  }

  return {
    control,
    errors,
    isValidating: isLoading,
    onSubmit: handleSubmit(onSubmit),
    reset,
  }
}
